<template>
    <div class="PracticeVerticalTimeline" :class="{ extendTop, trim }">
        <div
            v-for="(item, index) in items"
            :key="index"
            class="Step"
            :class="{ 'is-active': index === activeIndex }"
            @click.self="$emit('click', index)"
        >
            <div class="bullet" :class="[`align-${item.align}`]">
                <div
                    v-if="extendTop && index === 0"
                    class="extendedTopLine"
                    :style="item.progress ? activeLine2Style : line2Style"
                />
                <div class="line1" :style="checkLine1Active(index) ? activeLine1Style : line1Style" />
                <slot name="bullet" :type="item.type" :item="item" :index="index" />
                <div class="line2" :style="checkLine2Active(index) ? activeLine2Style : line2Style" />
            </div>
            <div class="content" :class="{ 'use-overflow-hidden': contentOverflowHidden }">
                <slot name="content" :item="item" :text="item.text" :index="index">
                    <div class="content-wrapper">
                        <p>{{ 'Step ' + (index + 1) }}</p>
                        <div class="headerTitle">
                            {{ item.title }}
                        </div>
                        <div class="icons">
                            <a
                                v-for="(scenario, i) in item.scenarios"
                                :key="i"
                                class="icon"
                                :style="{
                                    background: backgroundColor(i, item.id, scenario.done),
                                    border: !scenario.done && '1px solid #D9E4F5'
                                }"
                                :class="{
                                    clickable:
                                        scenario.done ||
                                        (currentScenarioInfo &&
                                            i === currentScenarioInfo.scenarioIndex &&
                                            currentScenarioInfo.stepId === item.id)
                                }"
                                @click="
                                    !didUserScenarioToday &&
                                    (isJourneyUnlocked || journeyIsLockedButHasProgress) &&
                                    currentScenarioInfo.stepId === item.id
                                        ? gotoScenario(item.id, i, scenario.done)
                                        : null
                                "
                            >
                                <svgicon
                                    class="leaf"
                                    icon="leaf"
                                    :style="{
                                        fill: iconColor(i, item.id, scenario.done)
                                    }"
                                />
                            </a>
                        </div>
                    </div>
                    <div class="button-wrapper">
                        <a
                            v-if="!journeyIsLockedButHasProgress"
                            class="button-blue"
                            @click="isClickable(item) ? gotoLastActivity(item) : null"
                        >
                            {{ isClickable(item) ? 'Start' : 'Locked' }}
                        </a>
                    </div>
                </slot>
            </div>
        </div>
    </div>
</template>

<script>
import { mapGetters } from 'vuex';
import moment from 'moment';

function gradientFromDasharray(dasharray = [], color, rotate = 0, reverse = false) {
    if (reverse) {
        dasharray = dasharray.slice().reverse();
    }
    const args = [`${rotate}deg`];
    let lastStop = 0;
    dasharray.forEach((d, i) => {
        const c = i % 2 ^ reverse ? color : 'transparent';
        args.push(`${c} ${lastStop}px`);
        args.push(`${c} ${lastStop + d}px`);
        lastStop += d;
    });
    return `repeating-linear-gradient(${args.join(',')})`;
}

const defaultLineStyle = {
    color: 'rgba(0, 0, 0, 0.05)',
    width: 2
};

export default {
    props: {
        items: {
            type: Array
        },
        lineStyle: {
            type: Object,
            default() {
                return {};
            }
        },
        activeLineStyle: {
            type: Object,
            default() {
                return {};
            }
        },
        contentOverflowHidden: {
            type: Boolean,
            default: true
        },
        activeIndex: {
            type: Number,
            default: -1
        },
        trim: {
            type: Boolean,
            default: false
        },
        extendTop: {
            type: Boolean,
            default: false
        },
        courseId: {
            type: String,
            required: true
        },
        isJourneyUnlocked: {
            type: Boolean,
            required: true
        },
        journeyIsLockedButHasProgress: {
            type: Boolean,
            required: true
        },
        didUserScenarioToday: {
            type: Boolean,
            required: true
        }
    },
    computed: {
        course() {
            return this.$store.state.moduleCourse.courses[this.courseId];
        },
        currentScenarioInfo() {
            return this.course.currentScenarioInfo;
        },
        line1Style() {
            const color = this.lineStyle.color || defaultLineStyle.color;
            return {
                backgroundColor: color,
                width: (this.lineStyle.width || defaultLineStyle.width) + 'px',
                background: gradientFromDasharray(this.lineStyle.dasharray, color, 180)
            };
        },
        line2Style() {
            const color = this.lineStyle.color || defaultLineStyle.color;
            return {
                backgroundColor: color,
                width: (this.lineStyle.width || defaultLineStyle.width) + 'px',
                background: gradientFromDasharray(this.lineStyle.dasharray, color, 0, true)
            };
        },
        activeLine1Style() {
            const color = this.activeLineStyle.color || defaultLineStyle.color;
            return {
                backgroundColor: color,
                width: (this.activeLineStyle.width || defaultLineStyle.width) + 'px',
                background: gradientFromDasharray(this.activeLineStyle.dasharray, color, 180)
            };
        },
        activeLine2Style() {
            const color = this.activeLineStyle.color || defaultLineStyle.color;
            return {
                backgroundColor: color,
                width: (this.activeLineStyle.width || defaultLineStyle.width) + 'px',
                background: gradientFromDasharray(this.activeLineStyle.dasharray, color, 0, true)
            };
        }
    },

    methods: {
        iconColor(iconIndex, itemId, isScenarioDone) {
            if (
                !this.didUserScenarioToday &&
                this.currentScenarioInfo &&
                iconIndex === this.currentScenarioInfo.scenarioIndex &&
                this.currentScenarioInfo.stepId === itemId
            ) {
                return '#5983f7';
            } else if (isScenarioDone) {
                return 'white';
            } else {
                return '#D9E4F5';
            }
        },
        backgroundColor(scenarioIndex, itemId, isScenarioDone) {
            if (
                !this.didUserScenarioToday &&
                this.currentScenarioInfo &&
                scenarioIndex === this.currentScenarioInfo.scenarioIndex &&
                this.currentScenarioInfo.stepId === itemId
            ) {
                return '#EBF2FD';
            } else if (isScenarioDone) {
                return '#5983f7';
            } else {
                return 'white';
            }
        },
        checkLine1Active(itemIndex) {
            const item = this.items[itemIndex];
            if (itemIndex === 0 && item.progress) {
                return true;
            }
            if (itemIndex > 0 && this.items[itemIndex - 1].progress === 1 && item.progress) {
                return true;
            }
            return false;
        },
        checkLine2Active(itemIndex) {
            const item = this.items[itemIndex];
            const nextItem = this.items[itemIndex + 1];
            if (nextItem && item.progress === 1 && nextItem.progress) {
                return true;
            }
            return false;
        },
        gotoScenario(stepId, scenarioIndex, isScenarioDone) {
            if (
                isScenarioDone ||
                (this.currentScenarioInfo && this.currentScenarioInfo.scenarioIndex === scenarioIndex)
            ) {
                this.$router.push({
                    name: 'DashboardScenarioView',
                    params: { lifeskillId: this.course.lifeskillNumber, stepId, scenarioIndex }
                });
            }
        },
        gotoLastActivity(step) {
            const containsCourseLastScenario = this.currentScenarioInfo && step.id === this.currentScenarioInfo.stepId;

            if (containsCourseLastScenario) {
                const scenarioIndex = this.currentScenarioInfo ? this.currentScenarioInfo.scenarioIndex : 0;

                this.$router.push({
                    name: 'DashboardScenarioView',
                    params: { lifeskillId: this.course.lifeskillNumber, stepId: step.id, scenarioIndex }
                });
            } else {
                const doneStepScenarios = step.scenarios.filter(scenario => scenario.done);

                this.$router.push({
                    name: 'DashboardScenarioView',
                    params: {
                        lifeskillId: this.course.lifeskillNumber,
                        stepId: step.id,
                        scenarioIndex: doneStepScenarios.length - 1
                    }
                });
            }
        },
        isClickable(item) {
            return (
                !this.didUserScenarioToday &&
                ((this.isJourneyUnlocked && item.scenarios.some(scenario => scenario.done)) ||
                    (this.isJourneyUnlocked && this.currentScenarioInfo && this.currentScenarioInfo.stepId === item.id))
            );
        }
    }
};
</script>

<style lang="scss" scoped>
$margin: 0.8em;

.PracticeVerticalTimeline {
    &.extendTop {
        .Step:first-child .line1 {
            visibility: visible;
        }
    }
    &.trim {
        .Step {
            &:first-child {
                .content {
                    margin-top: 0;
                }
                .line1 {
                    min-height: 0;
                }
                .line2 {
                    flex-basis: $margin;
                }
            }
            &:last-child {
                .content {
                    margin-bottom: 0;
                }
                .line1 {
                    flex-basis: $margin;
                }
                .line2 {
                    min-height: 0;
                }
            }
        }
    }
}

.Step {
    position: relative;
    display: flex;
    align-items: center;

    &:first-child {
        .line1 {
            visibility: hidden;
        }
    }
    &:last-child {
        .line2 {
            visibility: hidden;
        }
    }
}

.content-wrapper {
    padding-right: 20px;
    .icons {
        display: flex;
        margin-top: 0.94em;
        .icon {
            width: 2.2em;
            height: 2.2em;
            border-radius: 50%;
            display: flex;
            justify-content: center;
            align-items: center;
            margin-right: 0.31em;
            @media (max-width: 530px) {
                width: 29px;
                height: 29px;
            }

            .leaf {
                width: 0.97em;
                height: 0.97em;
            }
        }
        .clickable {
            cursor: pointer;
            &:hover {
                animation: pulse-animation2 3s ease 1s infinite;
                $shadowColor: $purple;
                box-shadow: 0 0 0 0 rgba($shadowColor, 0.5);

                @keyframes pulse-animation2 {
                    0% {
                        box-shadow: 0 0 0 0 var(--local-color, $shadowColor);
                    }
                    50% {
                        box-shadow: 0 0 0 0.5em transparent;
                    }
                    100% {
                        box-shadow: 0 0 0 0 transparent;
                    }
                }
            }
        }
    }
}

.button-wrapper {
    display: flex;
    align-items: center;
    .button-blue {
        cursor: pointer;
    }
}

.headerTitle {
    font-size: 140%;
    font-weight: 600;
    @media (max-width: 530px) {
        font-size: 16px;
    }
}

.bullet {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    align-self: stretch;
    flex-shrink: 0;
    min-width: 66px;

    &.align-top {
        .line1 {
            display: none;
        }
    }
}

.extendedTopLine {
    // NOTE: Must get height from outside this scope
    position: absolute;
    top: 0;
    transform: translateY(-100%);
}

.line1,
.line2 {
    min-height: $margin;
    flex: 1;
}

.content {
    display: flex;
    width: 100%;
    justify-content: space-between;
    padding-top: 1.88em;
    padding-bottom: 1.25em;
    border-bottom: 1px solid rgba(133, 133, 133, 0.12);
    p {
        font-size: 1em;
        line-height: 1.38em;
        color: $grayText;
        margin: 0;
        @media (max-width: 530px) {
            font-size: 14px;
        }
    }

    &.use-overflow-hidden {
        overflow: hidden; // NOTE: needed for ellipsis on children
    }
}
</style>
